// =============================================================================
// MatchClearedView.ts — Apuração vencida (issue #85): tela de parabéns com as
// estatísticas da chapa e a escolha fatal — SAIR ou MODO INFINITO.
// Com santinho compartilhável da vitória (issue #96).
// =============================================================================
import type { MatchClearedPayload } from '../../../shared/protocol';
import { WEAPON_TABLE } from '../../../shared/weapons';
import { UPGRADE_TABLE } from '../../../shared/upgrades';
import { CONQUISTA_TABLE } from '../../../shared/feitos';
import { $, $in, charImg, type Overlay } from './dom';
import { caixa2Icon, seloIcon } from './pixelIcons';
import { SantinhoPanel, honorifico } from './santinho';

const fmt = (n: number): string => n.toLocaleString('pt-BR');

export class MatchClearedView implements Overlay {
  // Raiz única do overlay: todos os ids desta view moram dentro do #cleared
  readonly root: HTMLElement = $('cleared');
  private readonly q = $in(this.root);
  private readonly santinho = new SantinhoPanel();

  show(data: MatchClearedPayload, youId: string | null, onExit: () => void, onInfinite: () => void): void {
    this.q('cl-frase').textContent = `“${data.frase}”`;

    const box = this.q('cl-stats');
    const mm = Math.floor(data.timeSurvived / 60);
    const ss = data.timeSurvived % 60;
    const totalKills = data.stats.reduce((n, s) => n + s.kills, 0);
    box.innerHTML = `
      <div class="go-row"><span>Tempo de apuração</span><b>${mm}m ${ss}s</b></div>
      <div class="go-row"><span>Nível da chapa</span><b>${data.level}</b></div>
      <div class="go-row"><span>Adversários neutralizados (chapa)</span><b>${totalKills}</b></div>`;

    // Armas supremas conquistadas via GOLPE DE MESTRE (#98)
    if (data.evolutions?.length) {
      const supremas = data.evolutions
        .map((id) => WEAPON_TABLE[id]?.nome ?? id)
        .join(' · ');
      box.innerHTML += `
        <div class="go-row evo-row"><span>Armas supremas da chapa</span><b>${supremas}</b></div>`;
    }

    for (const s of data.stats) {
      const armas = s.weapons
        .map((w) => {
          const def = WEAPON_TABLE[w.id];
          // Arma suprema (#98) tem nível único: selo no lugar do "nv X"
          return `${def?.nome ?? w.id} <b>${def?.evolved ? 'SUPREMA' : `nv ${w.lvl}`}</b>`;
        })
        .join(' &middot; ');
      const passivas = Object.entries(s.upgrades)
        .map(([id, n]) => `${UPGRADE_TABLE[id]?.nome ?? id}${n > 1 ? ` <b>x${n}</b>` : ''}`)
        .join(' &middot; ');
      box.innerHTML += `
        <div class="cl-player">
          <div class="go-row"><span>${charImg(s.charId, 30, false, s.skinId)} ${s.name}</span>
            <span><b>${s.score.toLocaleString('pt-BR')}</b> pontos · <b>${s.kills}</b> adversários neutralizados</span></div>
          <div class="cl-loadout">${armas}</div>
          ${passivas ? `<div class="cl-loadout">${passivas}</div>` : ''}
        </div>`;
    }

    // Meta-progressão (#90/#94): resumo do que foi creditado no perfil de
    // cada um (o modo infinito credita depois só o que vier a mais)
    box.innerHTML += '<div class="go-meta-titulo">CREDITADO NO CURRAL ELEITORAL</div>';
    for (const s of data.stats) {
      box.innerHTML += `
        <div class="go-row go-meta">
          <span>${s.name}</span>
          <span>+<b>${fmt(s.votosGanhos ?? 0)}</b> votos vitalícios · ${caixa2Icon(13)} +<b>${fmt(s.caixa2Ganhos ?? 0)}</b> de Caixa 2</span>
        </div>`;
    }

    // Promessas de Campanha cumpridas na SUA run (#99) — o Diário Oficial
    // publica a íntegra depois; aqui vai só a manchete
    const me = data.stats.find((s) => s.id === youId) ?? data.stats[0];
    for (const id of me?.conquistas ?? []) {
      const c = CONQUISTA_TABLE[id];
      if (!c) continue;
      box.innerHTML += `
        <div class="go-conquista">${seloIcon(18)}
          <span>PROMESSA CUMPRIDA: <b>${c.nome}</b> (+${fmt(c.votos)} votos)</span>
        </div>`;
    }

    // Santinho da vitória (#96): apuração 100% vencida direto pro grupo do zap
    const you = data.stats.find((s) => s.id === youId);
    const santinhoBox = this.q('cl-santinho');
    if (you) {
      this.santinho.render(santinhoBox, {
        name: you.name,
        charId: you.charId,
        kills: you.kills,
        level: data.level,
        timeSurvived: data.timeSurvived,
        titulo: honorifico(you, data.stats),
        resultado: '100% VENCIDA',
        resultadoBom: true,
      });
    } else {
      santinhoBox.innerHTML = '';
    }

    this.root.style.display = '';
    this.q('btn-cl-exit').onclick = onExit;
    this.q('btn-cl-infinite').onclick = onInfinite;
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  visible(): boolean {
    return this.root.style.display !== 'none';
  }
}
